import { Text, Textarea } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { EmptyState } from '../../../components/EmptyState'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { ApiErrorShape, formatDateTime, useAppToast } from '../../../helpers'
import { IBrilhaMagentaAiPrompt } from '../../../interfaces/BrilhaMagenta/IBrilhaMagentaAiPrompt'
import BrilhaMagentaService from '../../../services/BrilhaMagenta/BrilhaMagentaService'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Edita o prompt que a IA segue para sugerir os registros do Brilha a partir da transcrição.
 * O formato da resposta fica no backend e não passa por aqui, então uma edição não quebra a
 * integração.
 */
export function BrilhaMagentaAiPromptModal({ isOpen, onClose }: IProps) {
  const service = new BrilhaMagentaService()
  const toast = useAppToast()
  const [prompt, setPrompt] = useState<IBrilhaMagentaAiPrompt>()
  const [content, setContent] = useState('')
  const [contentError, setContentError] = useState<string | string[]>()
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const loadPrompt = async () => {
    setIsLoading(true)
    setLoadError(false)

    try {
      const data = await service.getAiPrompt()
      setPrompt(data)
      setContent(data.content)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar o prompt', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return

    setContentError(undefined)
    loadPrompt()
  }, [isOpen])

  const handleSave = async () => {
    if (!content.trim()) {
      setContentError('O prompt não pode ficar vazio.')
      return
    }

    setIsSaving(true)
    setContentError(undefined)

    try {
      const data = await service.updateAiPrompt(content)
      setPrompt(data)
      setContent(data.content)
      toast.success('Prompt salvo')
      onClose()
    } catch (error) {
      const { status, data } = (error as ApiErrorShape).response ?? {}
      const messages = data?.errors?.content

      if ((status === 400 || status === 422) && messages) {
        setContentError(messages)
      } else {
        toast.error('Erro ao salvar o prompt', error)
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Prompt da IA"
      size="editor"
      onSubmit={handleSave}
      confirmAction="save"
      isLoading={isSaving}
      isConfirmDisabled={isLoading || loadError}
    >
      {isLoading ? (
        <LoadingBlock entity="prompt" />
      ) : loadError ? (
        <EmptyState
          tone="error"
          icon="lni lni-warning"
          title="Não foi possível carregar o prompt"
          action={<ActionButton action="refresh" label="Tentar novamente" showLabel mr={0} onClick={loadPrompt} />}
        />
      ) : (
        <>
          <Text variant="caption" mb="4">
            Instruções que a IA segue para sugerir os registros do Brilha Magenta a partir da transcrição da conversa. O
            formato da resposta (os quatro campos e o aviso de transcrição inválida) é controlado pelo sistema, nas
            instruções fixas abaixo do prompt, e não precisa estar aqui.
          </Text>
          <FormField
            label="Prompt"
            error={contentError}
            isRequired
            helperText={
              prompt?.updated_by_name
                ? `Alterado por ${prompt.updated_by_name} em ${formatDateTime(prompt.updated_at)}`
                : 'Texto original, ainda não alterado pelo painel.'
            }
          >
            <Textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              isDisabled={isSaving}
              minH="55vh"
              fontFamily="mono"
              fontSize="sm"
            />
          </FormField>
          <FormField
            label="Instruções fixas do sistema"
            helperText="Enviadas sempre depois do prompt e não editáveis; prevalecem sobre ele."
            mt="4"
          >
            <Textarea
              value={prompt?.addendum ?? ''}
              isReadOnly
              minH="30vh"
              fontFamily="mono"
              fontSize="sm"
              bg="gray.50"
            />
          </FormField>
        </>
      )}
    </FormModal>
  )
}
